import { io, Socket } from 'socket.io-client';
import { GameEvents } from '../shared/constants';
import { logDebug } from '../utils/logger';
import type { SocketConfig } from '../utils/socketUrl';

class SocketService {
  socket: Socket | null = null;

  connect(config: SocketConfig, existingSocket: Socket | null = null): Socket {
    if (existingSocket) {
      this.socket = existingSocket;
      logDebug('Using existing socket connection', this.socket.id);
      return this.socket;
    }
    if (this.socket === null) {
      this.socket = io(config.url, config.options);
      this.socket.on('connect', () => {
        logDebug('Connected to socket server', this.socket!.id);
      });
    }
    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  playerUpdate(positionData: any): void {
    if (this.socket) {
      // Consolidated into game:action stream; volatile to reduce backpressure
      (this.socket as any).volatile.emit(GameEvents.GAME_ACTION, 'player:update', positionData);
    }
  }

  AddPlayer(username: string, color: string): void {
    if (this.socket) {
      logDebug('Frontend emitting: player:spawn', { name: username, color });
      this.socket.emit(GameEvents.GAME_ACTION, 'player:spawn', { name: username, color });
    }
  }

  updateName(name: string): void {
    if (this.socket) {
      logDebug('Frontend emitting: updateName', name);
      this.socket.emit(GameEvents.UPDATE_NAME, name);
    }
  }

  fireProjectile(turretPosition: any, turretDirection: any, weapon?: string): void {
    if (this.socket) {
      const data: any = { turretPosition, turretDirection: this.quatToObject(turretDirection) };
      if (weapon) data.weapon = weapon;
      logDebug('Frontend emitting: fireProjectile', data);
      this.socket.emit(GameEvents.GAME_ACTION, 'projectile:fire', data);
    }
  }

  dealDamage(damage: number, userId: string): void {
    if (this.socket) {
      const data = { damage, userId };
      logDebug('Frontend emitting: dealDamage', data);
      this.socket.emit(GameEvents.GAME_ACTION, 'damage:deal', data);
    }
  }

  playerDead(): void {
    if (this.socket) {
      logDebug('Frontend emitting: playerDead');
      this.socket.emit(GameEvents.GAME_ACTION, 'player:dead', {});
    }
  }

  requestPlayers(): void {
    if (this.socket) {
      logDebug('Frontend emitting: requestPlayers');
      this.socket.emit(GameEvents.REQUEST_PLAYERS, {});
    }
  }

  // Generic game:action sender
  sendAction(type: string, data?: any): void {
    if (this.socket) {
      try {
        this.socket.emit(GameEvents.GAME_ACTION, type, data ?? {});
      } catch {}
    }
  }

  getSocketId(): string | null {
    return this.socket ? this.socket.id : null;
  }

  private quatToObject(quat: { x: number; y: number; z: number; w: number }) {
    return { x: quat.x, y: quat.y, z: quat.z, w: quat.w };
  }
}

const socketService = new SocketService();
export default socketService;
